import { useState } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import Paper from '@base/Paper/Paper';
import { mediaQuery, Spacings } from '@styles/index';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import Divider from '@base/Divider/Divider';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getQuizSyllabus, getQuizDetails } from '@selectors/quizTaking';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import KeyboardArrowUp from '@icon/KeyboardArrowUp';
import { getIsGTPGoal } from '@api/selectors/constants';
import { getTopologyDataByUID } from '@selectors/topologies';
import { MIN_SUBJECTS } from '@cont/Quiz/constants';
import Dynamic from '@base/Dynamic/Dynamic';
import useConstants from '@api/hooks/useConstants';
import useProgrammeType from '@cont/Quiz/Common/useProgramme';
import SyllabusPracticePanel from './SyllabusPracticePanel';

const Collapse = Dynamic(() => import('@material-ui/core/Collapse/Collapse'));

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_14B};
  grid-column: span 24;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mediaQuery.uptoTablet} {
    grid-column: auto;
    width: 100%;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const Heading = styled(H4)`
  padding-left: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoTablet} {
    padding-left: ${Spacings.SPACING_4B};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacings.SPACING_8B};
  width: 100%;

  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    align-items: stretch;
    gap: ${Spacings.SPACING_4B};
  }
`;

const SubjectWrapper = styled(Paper)`
  padding: ${Spacings.SPACING_8B};
  padding-bottom: 0;
  margin: ${Spacings.SPACING_6B} ${Spacings.SPACING_19B} ${Spacings.SPACING_6B}
    0;
  flex: 3;
  min-width: 0;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    flex: unset;
    margin-bottom: 0;
  }
`;

const ListWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const ListDivider = styled(Divider)`
  margin: ${Spacings.SPACING_4B} 0;
`;

const SubjectName = styled(H5)`
  padding-bottom: ${Spacings.SPACING_4B};
`;

const ConceptList = styled.ul`
  padding-inline-start: 20px;
`;

const ConceptName = styled.li`
  padding-bottom: ${Spacings.SPACING_2B};
  color: var(--color-text-primary);
`;

const StyledP1 = styled(P1)`
  padding-bottom: ${Spacings.SPACING_2B};
`;

const ViewMoreWrapper = styled.div`
  margin: auto;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  padding: ${Spacings.SPACING_2B} 0 ${Spacings.SPACING_10B};
`;

const ViewText = styled(H6)`
  padding-right: ${Spacings.SPACING_1B};
`;

const showDivider = (index, listLength) => index !== listLength - 1;

const ViewMore = ({ expand, setExpand }) => {
  const expandOnClick = () => setExpand((prev) => !prev);

  return (
    <>
      <ViewMoreWrapper onClick={expandOnClick}>
        <ViewText>View {expand ? 'less' : 'more'}</ViewText>
        {expand ? (
          <KeyboardArrowUp size="20px" />
        ) : (
          <KeyboardArrowDown size="20px" />
        )}
      </ViewMoreWrapper>
    </>
  );
};

const Syllabus = () => {
  const { query } = useRouter();
  const { quizUID } = query;
  const [expand, setExpand] = useState(false);

  const quizInfo = useSelectorWithShallowEqual(getQuizDetails(quizUID));

  const isGTP = useConstants(getIsGTPGoal(quizInfo?.goal_uid));
  const { isTestSeries } = useProgrammeType();
  const programme = quizInfo?.collection?.programme;
  const goalDetails = useSelectorWithShallowEqual(
    getTopologyDataByUID(quizInfo?.goal_uid)
  );

  const syllabus = useSelectorWithShallowEqual(getQuizSyllabus(quizUID));
  const showSection = syllabus.length > 0;

  const renderConcepts = (concepts) => {
    return concepts.map((concept, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <ConceptName key={i}>{concept}</ConceptName>
    ));
  };

  const minIndices = Math.min(syllabus.length, MIN_SUBJECTS);
  const maxIndices = syllabus.length - 1 - MIN_SUBJECTS;
  const showPracticePanel = isTestSeries && !!programme?.uid;

  const renderSubjects = (startIndex, length) => {
    if (isGTP) {
      return (
        <ListWrapper>
          <SubjectName>{goalDetails.name}</SubjectName>
          <StyledP1>Complete syllabus</StyledP1>
        </ListWrapper>
      );
    }
    const subjectList = [];

    for (let i = startIndex; i < length; i += 1) {
      subjectList.push(
        <ListWrapper key={i}>
          <SubjectName>{syllabus[i].name}</SubjectName>
          <ConceptList>{renderConcepts(syllabus[i].concepts)}</ConceptList>
          {showDivider(i, syllabus.length) && <ListDivider />}
        </ListWrapper>
      );
    }
    return subjectList;
  };

  return (
    <>
      {showSection && (
        <Wrapper>
          <ContentWrapper>
            <Heading>Syllabus</Heading>
            <SubjectWrapper>
              {renderSubjects(0, minIndices)}
              {!isGTP && syllabus.length > MIN_SUBJECTS && (
                <>
                  <Collapse in={expand}>
                    {renderSubjects(maxIndices, syllabus.length)}
                  </Collapse>
                  <ViewMore expand={expand} setExpand={setExpand} />
                </>
              )}
            </SubjectWrapper>
            {showPracticePanel && (
              <SyllabusPracticePanel
                programmeUID={programme.uid}
                goalUID={programme.goal?.uid}
                goalSlug={programme.goal?.slug}
              />
            )}
          </ContentWrapper>
        </Wrapper>
      )}
    </>
  );
};

export default Syllabus;
